import { TaxBracket, ContributionRates } from '../types';

// 2024 Philippine Tax Brackets (BIR)
export const PHILIPPINE_TAX_BRACKETS: TaxBracket[] = [
  { min_income: 0, max_income: 250000, base_tax: 0, rate: 0 },
  { min_income: 250000, max_income: 400000, base_tax: 0, rate: 0.20 },
  { min_income: 400000, max_income: 800000, base_tax: 30000, rate: 0.25 },
  { min_income: 800000, max_income: 2000000, base_tax: 130000, rate: 0.30 },
  { min_income: 2000000, max_income: 8000000, base_tax: 490000, rate: 0.32 },
  { min_income: 8000000, max_income: Infinity, base_tax: 2410000, rate: 0.35 }
];

// 2024 Philippine Government Contribution Rates
export const PHILIPPINE_CONTRIBUTION_RATES: ContributionRates = {
  sss: {
    employee_rate: 0.045, // 4.5%
    employer_rate: 0.095, // 9.5%
    min_salary: 4000,
    max_salary: 30000
  },
  philhealth: {
    employee_rate: 0.0225, // 2.25%
    employer_rate: 0.0225, // 2.25%
    min_contribution: 450,
    max_contribution: 4050
  },
  pagibig: {
    employee_rate: 0.02, // 2%
    employer_rate: 0.02, // 2%
    max_contribution: 200
  }
};

/**
 * Calculate SSS contribution based on monthly salary
 */
export function calculateSSSContribution(monthlySalary: number): number {
  const { sss } = PHILIPPINE_CONTRIBUTION_RATES;
  const contributionSalary = Math.min(Math.max(monthlySalary, sss.min_salary), sss.max_salary);
  return Math.round(contributionSalary * sss.employee_rate);
}

/**
 * Calculate PhilHealth contribution based on monthly salary
 */
export function calculatePhilHealthContribution(monthlySalary: number): number {
  const { philhealth } = PHILIPPINE_CONTRIBUTION_RATES;
  const contribution = monthlySalary * philhealth.employee_rate;
  return Math.round(Math.min(Math.max(contribution, philhealth.min_contribution), philhealth.max_contribution));
}

/**
 * Calculate Pag-IBIG contribution based on monthly salary
 */
export function calculatePagIBIGContribution(monthlySalary: number): number {
  const { pagibig } = PHILIPPINE_CONTRIBUTION_RATES;
  const contribution = monthlySalary * pagibig.employee_rate;
  return Math.round(Math.min(contribution, pagibig.max_contribution));
}

/**
 * Calculate withholding tax based on annual taxable income
 */
export function calculateWithholdingTax(annualTaxableIncome: number): number {
  for (const bracket of PHILIPPINE_TAX_BRACKETS) {
    if (annualTaxableIncome >= bracket.min_income && annualTaxableIncome < bracket.max_income) {
      const excessIncome = annualTaxableIncome - bracket.min_income;
      const tax = bracket.base_tax + (excessIncome * bracket.rate);
      return Math.round(tax);
    }
  }
  return 0;
}

/**
 * Calculate monthly withholding tax
 */
export function calculateMonthlyWithholdingTax(monthlySalary: number, monthsWorked: number = 12): number {
  const annualSalary = monthlySalary * monthsWorked;
  const annualTax = calculateWithholdingTax(annualSalary);
  return Math.round(annualTax / monthsWorked);
}

/**
 * Calculate total hours worked from clock in/out times
 */
export function calculateHoursWorked(clockIn: string, clockOut: string, breakStart?: string, breakEnd?: string): {
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
} {
  if (!clockIn || !clockOut) {
    return { totalHours: 0, regularHours: 0, overtimeHours: 0 };
  }

  const clockInTime = new Date(clockIn);
  const clockOutTime = new Date(clockOut);
  
  let totalMinutes = (clockOutTime.getTime() - clockInTime.getTime()) / (1000 * 60);
  
  // Subtract break time if provided
  if (breakStart && breakEnd) {
    const breakStartTime = new Date(breakStart);
    const breakEndTime = new Date(breakEnd);
    const breakMinutes = (breakEndTime.getTime() - breakStartTime.getTime()) / (1000 * 60);
    totalMinutes -= breakMinutes;
  }
  
  const totalHours = totalMinutes / 60;
  const regularHours = Math.min(totalHours, 8); // Standard 8-hour workday
  const overtimeHours = Math.max(0, totalHours - 8);
  
  return {
    totalHours: Math.round(totalHours * 100) / 100,
    regularHours: Math.round(regularHours * 100) / 100,
    overtimeHours: Math.round(overtimeHours * 100) / 100
  };
}

/**
 * Calculate overtime pay (1.25x for first 2 hours, 1.5x for additional hours)
 */
export function calculateOvertimePay(hourlyRate: number, overtimeHours: number): number {
  if (overtimeHours <= 0) return 0;
  
  let overtimePay = 0;
  
  // First 2 hours at 1.25x
  const firstTwoHours = Math.min(overtimeHours, 2);
  overtimePay += firstTwoHours * hourlyRate * 1.25;
  
  // Additional hours at 1.5x
  const additionalHours = Math.max(0, overtimeHours - 2);
  overtimePay += additionalHours * hourlyRate * 1.5;
  
  return Math.round(overtimePay * 100) / 100;
}

/**
 * Calculate daily rate from monthly salary
 */
export function calculateDailyRate(monthlySalary: number): number {
  return monthlySalary / 22; // Assuming 22 working days per month
}

/**
 * Calculate hourly rate from monthly salary
 */
export function calculateHourlyRate(monthlySalary: number): number {
  return monthlySalary / (22 * 8); // 22 working days, 8 hours per day
}

/**
 * Get attendance status based on clock in/out times
 */
export function getAttendanceStatus(clockIn: string | null, clockOut: string | null, date: string): 'present' | 'absent' | 'late' | 'half-day' | 'incomplete' {
  if (!clockIn) {
    return 'absent';
  }
  
  if (!clockOut) {
    return 'incomplete';
  }
  
  const clockInTime = new Date(clockIn);
  const lateThreshold = new Date(date + 'T08:15:00'); // 8:15 AM late threshold
  
  const { totalHours } = calculateHoursWorked(clockIn, clockOut);
  
  // Half day if worked less than 4 hours
  if (totalHours < 4) {
    return 'half-day';
  }
  
  // Late only if clocked in after 8:15 AM (not before work start time)
  const isLate = clockInTime.getTime() > lateThreshold.getTime();
  
  return isLate ? 'late' : 'present';
}

/**
 * Format currency in Philippine Peso
 */
export function formatPHP(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2
  }).format(amount);
}

/**
 * Calculate 13th month pay (1/12 of total basic salary for the year)
 */
export function calculate13thMonthPay(monthlySalary: number, monthsWorked: number = 12): number {
  // 13th month pay is calculated as (Basic Salary x Months Worked) / 12
  const thirteenthMonthPay = (monthlySalary * monthsWorked) / 12;
  return Math.round(thirteenthMonthPay * 100) / 100;
}

/**
 * Calculate net pay after all deductions
 */
export function calculateNetPay({
  basicSalary,
  overtimePay = 0,
  bonuses = 0,
  thirteenthMonthPay = 0,
  holidayPay = 0,
  holidayAdditionalPay = 0,
  sssContribution,
  philhealthContribution,
  pagibigContribution,
  withholdingTax,
  otherDeductions = 0
}: {
  basicSalary: number;
  overtimePay?: number;
  bonuses?: number;
  thirteenthMonthPay?: number;
  holidayPay?: number;
  holidayAdditionalPay?: number;
  sssContribution: number;
  philhealthContribution: number;
  pagibigContribution: number;
  withholdingTax: number;
  otherDeductions?: number;
}): {
  grossPay: number;
  totalDeductions: number;
  netPay: number;
} {
  const grossPay = basicSalary + overtimePay + bonuses + thirteenthMonthPay + holidayPay + holidayAdditionalPay;
  const totalDeductions = sssContribution + philhealthContribution + pagibigContribution + withholdingTax + otherDeductions;
  const netPay = grossPay - totalDeductions;
  
  return {
    grossPay: Math.round(grossPay * 100) / 100,
    totalDeductions: Math.round(totalDeductions * 100) / 100,
    netPay: Math.round(netPay * 100) / 100
  };
}